export const lensProp = <T, K extends keyof T>(prop: K) => ({
  get: (obj: T) => obj[prop],
  set:
    (value: T[K]) =>
    (obj: T): T => ({
      ...obj,
      [prop]: value
    }),
  asOptional() {
    // For compatibility with your compose usage
    return {
      get: this.get,
      set: this.set
    }
  }
})

export const groupLens = <T, G, K extends keyof T>(
  groupId: string,
  groupsKey: K,
  create: (groups: Record<string, G>, ...rest: any[]) => T,
  getRest: (s: T) => any[]
) => ({
  getOption: (s: T): G | undefined => (s[groupsKey] as Record<string, G>)[groupId],

  set:
    (a: G) =>
    (s: T): T => {
      const groups = { ...(s[groupsKey] as Record<string, G>), [groupId]: a }
      return create(groups, ...getRest(s))
    },

  compose<B>(optic: { get: (a: G) => B; set: (b: B) => (a: G) => G }) {
    return {
      getOption: (s: T): B | undefined => {
        const group = this.getOption(s)
        return group ? optic.get(group) : undefined
      },
      set:
        (b: B) =>
        (s: T): T => {
          const group = this.getOption(s)
          if (!group) {
            return s
          }
          const newGroup = optic.set(b)(group)
          return this.set(newGroup)(s)
        },
      modify:
        (f: (b: B) => B) =>
        (s: T): T => {
          const group = this.getOption(s)
          if (!group) {
            return s
          }
          const newGroup = optic.set(f(optic.get(group)))(group)
          return this.set(newGroup)(s)
        }
    }
  }
})

export const groupLens2 = <T, G, K extends keyof T>(
  groupId: string,
  groupsKey: K,
  create: (groups: Record<string, G>, ...rest: any[]) => T,
  getRest: (s: T) => any[]
) => ({
  getOption: (s: T): G | undefined => (s[groupsKey] as Record<string, G>)[groupId],

  // Add a get method for compatibility with lens interface
  get: (s: T): G | undefined => (s[groupsKey] as Record<string, G>)[groupId],

  set:
    (a: G) =>
    (s: T): T => {
      const groups = { ...(s[groupsKey] as Record<string, G>), [groupId]: a }
      return create(groups, ...getRest(s))
    },

  compose<B>(optic: { get: (a: G) => B; set: (b: B) => (a: G) => G }) {
    return {
      getOption: (s: T): B | undefined => {
        const group = this.getOption(s)
        return group ? optic.get(group) : undefined
      },
      get: (s: T): B | undefined => {
        const group = this.getOption(s)
        return group ? optic.get(group) : undefined
      },
      set:
        (b: B) =>
        (s: T): T => {
          const group = this.getOption(s)
          if (!group) {
            return s
          }
          const newGroup = optic.set(b)(group)
          return this.set(newGroup)(s)
        },
      modify:
        (f: (b: B) => B) =>
        (s: T): T => {
          const group = this.getOption(s)
          if (!group) {
            return s
          }
          const newGroup = optic.set(f(optic.get(group)))(group)
          return this.set(newGroup)(s)
        }
    }
  }
})

export function asLens<A, B>(optic: { getOption: (a: A) => B; set: (b: B) => (a: A) => A }) {
  return {
    get: optic.getOption,
    set: optic.set
  }
}
